import React, { useCallback, useMemo, useState } from 'react'
import { FullPanelOverlay } from '@components/Web3/WalletLinkingPanel'
import { Stack, TextField } from '@ui'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { Address } from 'use-towns-client'

import { useContractHasTransferMethod } from '../hooks/useContractHasTransferMethod'
import { isNativeAsset, useMergedAssets } from '../hooks/useMergedAssets'
import { useTradingWallet } from '../hooks/useTradingWallet'
import { useTradingWalletAddresses } from '../hooks/useTradingWalletAddresses'
import { tradingChains } from '../tradingConstants'
import { ChainWalletAssets, MergedTradingAsset } from '../tradingUtils'
import { Disclaimer } from '../ui/Disclaimer'

import { TransferEVMTokenForm } from './evm/TransferEVMTokenForm'
import { TransferSolanaForm } from './solana/TransferSolanaForm'
import { TransferTokenSelector } from './TransferTokenSelector'
import { TransferSuccessCallback } from './types'

const stableArray: ChainWalletAssets[] = []

export function TransferTokenForm() {
    const { evmWalletAddress } = useTradingWalletAddresses()
    const {
        data: chainWalletAssets,
        isLoading: isLoadingChainWalletAssets,
        invalidateWalletQuery,
        fetchLatestWalletData,
    } = useTradingWallet()

    const onTransferSuccess = useCallback(async () => {
        await invalidateWalletQuery()
        setSelectedAsset(undefined)
    }, [invalidateWalletQuery])

    const { mergeAssets } = useMergedAssets()
    const allAssets = mergeAssets(chainWalletAssets ?? stableArray)
    const transferrableAssets = useMemo(() => {
        return allAssets.filter((asset) => {
            const balance = BigInt(asset.balance)
            return (
                balance > 0n &&
                (asset.chain === '8453' ||
                    asset.symbol === tradingChains['solana-mainnet'].tokenSymbol)
            )
        })
    }, [allAssets])

    const [selectedAsset, setSelectedAsset] = useState<MergedTradingAsset | undefined>()

    if (isLoadingChainWalletAssets || !chainWalletAssets) {
        return <FullPanelOverlay withSpinner={false} />
    }

    return (
        <Stack grow gap>
            <TransferTokenSelector
                assets={transferrableAssets}
                selectedAsset={selectedAsset}
                setSelectedAsset={setSelectedAsset}
                invalidateWalletQuery={invalidateWalletQuery}
                fetchLatestWalletData={fetchLatestWalletData}
            />
            <Forms
                selectedAsset={selectedAsset}
                evmWalletAddress={evmWalletAddress}
                onTransferSuccess={onTransferSuccess}
            />
        </Stack>
    )
}

function Forms(props: {
    selectedAsset: MergedTradingAsset | undefined
    evmWalletAddress: Address | undefined
    onTransferSuccess: TransferSuccessCallback
}) {
    const { selectedAsset, evmWalletAddress, onTransferSuccess } = props
    const nativeAsset = isNativeAsset(selectedAsset)

    const { data: hasTransferMethod, isLoading: isLoadingHasTransferMethod } =
        useContractHasTransferMethod({
            contractAddress: selectedAsset?.tokenAddress as Address | undefined,
            enabled: !!selectedAsset && !nativeAsset,
        })

    const selectedAssetBalance = useMemo(() => {
        if (!selectedAsset) {
            return 0n
        }

        try {
            // these assets already have a balance formatted to the correct # of decimals
            return BigInt(selectedAsset.balance)
        } catch (error) {
            console.warn('Failed to parse asset balance:', selectedAsset.balance, error)
            return 0n
        }
    }, [selectedAsset])

    if (!selectedAsset) {
        return (
            <TextField
                disabled
                label="Amount"
                background="level2"
                inputWidth="100%"
                placeholder="Select a token"
            />
        )
    }

    if (isLoadingHasTransferMethod) {
        return (
            <Stack padding="md">
                <ButtonSpinner />
            </Stack>
        )
    }

    if (selectedAsset.symbol === tradingChains['solana-mainnet'].tokenSymbol) {
        return (
            <TransferSolanaForm
                balance={selectedAssetBalance}
                onTransferSuccess={onTransferSuccess}
            />
        )
    }

    if (!nativeAsset && !hasTransferMethod) {
        return <Disclaimer>The selected asset does not support transfers.</Disclaimer>
    }

    return (
        <TransferEVMTokenForm
            evmWalletAddress={evmWalletAddress}
            balance={selectedAssetBalance}
            symbol={selectedAsset.symbol}
            decimals={selectedAsset.decimals}
            contractAddress={selectedAsset.tokenAddress as Address}
            onTransferSuccess={onTransferSuccess}
        />
    )
}
